import React, { useCallback, useEffect, useState } from 'react';
import usePageTitle from '~/app/hooks/usePageTitle';
import { useAppHeader } from '~/app/modules/appHeader';
import List from './List';
import useTranslation from "~/app/hooks/useTranslation";
import { useStoreActions, useStoreState } from '~/store';
import Header from './Header';
import { useHistory, useParams } from 'react-router-dom';
import { IssueDetails, Machine, Service } from "~/types";
import EmptyMessage from './EmptyMessage'
import AddIssue from '~/issues/Add';
import classes from './style.module.scss';
import { route } from '~/app/helpers/route';
import { ROUTES } from '~/app/Router';
import { ServiceStatuses } from '~/app/data/statuses/services';
import CompleteServiceDialog from '../CompleteServiceDialog';
import CloseServiceDialog from '../CloseServiceDialog';
import { SOURCE_ISSUE_TYPE } from '~/app/data/statuses/issue';

export interface ServiceChecklistProps {
  serviceId?: Service['id'];
  subTitle?: string;
  backLink?: string | (() => void);
  isWorkOrderChecklist?: boolean;
}

export type ServiceChecklistComponent = React.FC<ServiceChecklistProps>

const ServiceChecklist: ServiceChecklistComponent = ({ serviceId: serviceIdProp, subTitle, backLink, isWorkOrderChecklist = false }) => {
  const { t } = useTranslation('serviceChecklist');

  const { setData } = useAppHeader();
  const params = useParams<{ id: Machine['id'], serviceId: Service['id'] }>();
  const id = params.id;
  const serviceId = serviceIdProp ?? params.serviceId;
  const machine = useStoreState(state => state.machine.data);
  const serviceIntervalId = useStoreState(state => state.service.data.serviceIntervalId);
  const service = useStoreState(state => state.service.data);
  const serviceChecklistUpdating = useStoreState(state => state.service.serviceChecklistUpdating);
  const updateCheckData = useStoreActions(actions => actions.serviceCheck.updateCheckData);
  const currentCheck = useStoreState(state => state.serviceCheck.currentCheck);
  const updateCurrentCheck = useStoreActions(actions => actions.serviceCheck.updateCurrentCheck);
  const fetchServiceChecklist = useStoreActions(actions => actions.service.fetchServiceChecklist);
  const hasSimplifiedWorkorder = useStoreState(state => state.service.hasSimplifiedWorkorder ?? false);
  const netWorkStatus = useStoreState(state => state.app.offline.status);
  const checksData = useStoreState(state => state.serviceCheck.checksData);
  const [openAddIssueDialog, setAddIssueDialog] = useState<boolean>(false);
  const history = useHistory();

  usePageTitle(t('title'));

  useEffect(() => {
    setData({
      title: t('title'),
      subTitle: subTitle ?? (machine.intern + " - " + machine.name).toUpperCase(),
      backLink: backLink ?? (history.length > 1 ? history.goBack : route(ROUTES.projects)),
      showUser: false,
      machineId: '',
      projectId: '',
      showMachineMessages: false,
      isIssueDetailPage: false,
      notification: false,
      search: false
    });
  }, [setData, machine, t, id, serviceId, history, subTitle, backLink]);

  useEffect(() => {
    if (service?.id !== serviceId && serviceIntervalId) {
      fetchServiceChecklist({
        serviceId: serviceId,
        serviceIntervalId: serviceIntervalId
      });
    }
  }, [service?.id, fetchServiceChecklist, serviceId, serviceIntervalId])

  const toggleAddIssueDialog = useCallback(() => {
    openAddIssueDialog && updateCurrentCheck(null);
    setAddIssueDialog(!openAddIssueDialog);
  }, [setAddIssueDialog, openAddIssueDialog, updateCurrentCheck]);

  const changeStatus = useCallback((check, status) => {
    updateCurrentCheck(check);
    if (status === 0 && !check.issues.length) {
      toggleAddIssueDialog();
    } else {
      check.status = status;
      updateCheckData({ ...check });
      updateCurrentCheck(null);
    }
  }, [toggleAddIssueDialog, updateCheckData, updateCurrentCheck]);

  const handleIssueSave = useCallback((id: IssueDetails['id']) => {
    if (currentCheck) {
      currentCheck.status = 0;
      currentCheck.issues = [...currentCheck.issues, id];
      updateCurrentCheck(currentCheck);
      updateCheckData({ ...currentCheck });
    }
  }, [currentCheck, updateCurrentCheck, updateCheckData]);

  const handleRegisterIssue = useCallback((check) => {
    updateCurrentCheck(check);
    toggleAddIssueDialog();
  }, [toggleAddIssueDialog, updateCurrentCheck])

  if (serviceChecklistUpdating) {
    return <EmptyMessage />;
  }

  return (
    <>
      <div className={false ? classes['disabled'] : ''}>
        <Header isWorkOrderChecklist={isWorkOrderChecklist}/>
        {checksData.length > 0 ? (
          <List list={checksData} onSelect={changeStatus} onRegisterIssue={handleRegisterIssue} isWorkOrderChecklist={isWorkOrderChecklist} />
        ) : (
          <EmptyMessage />
        )}
      </div>
      <AddIssue open={openAddIssueDialog} onClose={toggleAddIssueDialog} type={'issue'} sourceTypeId={SOURCE_ISSUE_TYPE.SERVICE_CHECKLIST_FELT_APP} onSave={handleIssueSave} machineId={service?.machineId} isWorkOrderChecklist={isWorkOrderChecklist} />
      {netWorkStatus === 0 && (
        <>
          {(service.status === ServiceStatuses.TRIGGERED && (hasSimplifiedWorkorder === true || isWorkOrderChecklist === true)) && <CloseServiceDialog />}
          {(service.status === ServiceStatuses.INPROGRESS && service.workOrderNo) && (
            <CompleteServiceDialog isWorkOrderChecklist={isWorkOrderChecklist} />
          )}
        </>
      )}
    </>
  );
};

export default ServiceChecklist
